import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useRouteMatch } from "react-router-dom";
import {
  Button,
  Col,
  Row,
  Container,
  Table,
  FormGroup,
  Input,
} from "reactstrap";
import "./index.css";
import ApiCaller from "api/apiCaller";
import Loading from "components/Loading";
import { UserInfo } from "model/user";
import api from "api";
import { ApiResponse } from "model/response";
import { DataSource, DatasourceDetailResponse } from "model/datasource";
import { Dataset, DatasetListingResponse } from "model/dataset";
import ReactPaginate from "react-paginate";
import EditbaleInput from "components/EditableInput";

const PAGE_LIMIT = 50;
interface DetailData {
  datasource: DataSource | null;
  loading: boolean;
}

interface ListingData {
  datasets: Array<Dataset>;
  page: number;
  total: number;
}

export default function DatasourceDetail(props: any) {
  const match = useRouteMatch("/datasource/:datasource_id");
  const params: any = match?.params;
  const datasourceId = params?.datasource_id;

  const companyId: any = api.getCurrentCompanyId();
  const [currentUser, setCurrentUser] = useState<UserInfo | null>(null);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [detailData, setDetailData] = useState<DetailData>({
    datasource: null,
    loading: true,
  });

  const [listingData, setListingData] = useState<ListingData>({
    datasets: [],
    page: 0,
    total: 0,
  });

  // permission
  const canEditDatasource = currentUser?.is_admin || currentUser?.is_company_admin;
  const canViewChangelog = currentUser?.is_admin || currentUser?.is_company_admin;

  useEffect(() => {
    initData();
  }, []);

  useEffect(() => {
    fetchData();
  }, [listingData?.page]);

  const fetchData = async () => {
    let url = `dataset?company_id=${companyId}&datasource_id=${datasourceId}&limit=${PAGE_LIMIT}&page=${listingData?.page}`;
    if (searchKeyword !== "") {
      url = url + `&keyword=${searchKeyword}`;
    }
    const res: ApiResponse = await ApiCaller.get(url);
    if (res?.status === "ok") {
      const data: DatasetListingResponse = res?.data;
      setListingData({
        ...listingData,
        ...{ datasets: data?.datasets, total: data?.total },
      });
    }
  };

  const runGetMetadata = async () => {
    const url = `datasource/${datasourceId}/scan/metadata?company_id=${companyId}`;
    const res = ApiCaller.get(url);
    alert('Datasource is being scanned! It will take a while to scan a whole datasource.')
  };

  const testConnection = async () => {
    const url = `datasource/${datasourceId}/connection`;
    const res = await ApiCaller.post(url);
    alert(res?.message);
  };

  const initData = async () => {
    const user = api.getCurrentUser();
    setCurrentUser(user);
    const res: ApiResponse = await ApiCaller.get(`datasource/${datasourceId}`);
    if (res?.status === "ok") {
      const data: DatasourceDetailResponse = res?.data;
      setDetailData({
        ...detailData,
        ...{
          datasource: data?.datasource,
          loading: false,
        },
      });
    } else {
      setDetailData({
        ...detailData,
        ...{
          loading: false,
        },
      });
    }
  };

  const deleteDatasource = async () => {
    setDetailData({
      ...detailData,
      ...{
        loading: true,
      },
    });
    const res: ApiResponse = await ApiCaller.delete(
      `datasource/${datasourceId}`
    );
    if (res?.status === "ok") {
      setDetailData({
        ...detailData,
        ...{
          loading: false,
        },
      });
      props.history.goBack();
    } else {
      setDetailData({
        ...detailData,
        ...{
          loading: false,
        },
      });
    }
  };

  if (detailData?.loading) return <Loading />;

  return (
    <Container className="datasource__detail">
      <Row>
        <Col xs={9}>
          <h3>{detailData.datasource?.name}</h3>
        </Col>
        {canEditDatasource &&
          <Col xs={3}>
            <div>
              <Button
                size="sm"
                color="primary"
                tag={Link}
                to={{ pathname: `/datasource/${datasourceId}/edit` }}
              >
                Edit Datasource
              </Button>{" "}
              {detailData.datasource?.datasource_type?.code_name === "google_bigquery" &&
                <Button size="sm" color="info" onClick={runGetMetadata}>
                  Scan Datasource Now
                </Button>
              }
              {detailData.datasource?.datasource_type?.code_name?.includes("rdbms") &&
                <Button size="sm" color="info" onClick={testConnection}>
                  Test DB Connection
                </Button>
              }
            </div>
          </Col>
        }
      </Row>
      <Row>
        <Table responsive className="datasource__info" hover>
          <thead>
            <tr><th>Datasource info</th></tr>
          </thead>
          <tbody>
            <tr key="datasource_type">
              <td className="datasource__info_attribute_label">Type</td>
              <td>{detailData?.datasource?.datasource_type?.name}</td>
            </tr>
            {detailData?.datasource?.datasource_type?.code_name === "google_bigquery" &&
              <tr key="datasource_project">
                <td className="datasource__info_attribute_label">Project ID</td>
                <td>{detailData?.datasource?.project}</td>
              </tr>
            }
            {detailData?.datasource?.datasource_type?.code_name === "google_bigquery" &&
              <tr key="datasource_default_region">
                <td className="datasource__info_attribute_label">Default region</td>
                <td>{detailData?.datasource?.default_region_name}</td>
              </tr>
            }
            {(detailData.datasource?.datasource_type?.code_name?.includes("rdbms") ||
              detailData.datasource?.datasource_type?.code_name === "mongo") &&
              <tr key="datasource_host">
                <td className="datasource__info_attribute_label">Host</td>
                <td>{detailData?.datasource?.datasource_host}</td>
              </tr>
            }
            {(detailData.datasource?.datasource_type?.code_name?.includes("rdbms") ||
              detailData.datasource?.datasource_type?.code_name === "mongo") &&
              <tr key="datasource_port">
                <td className="datasource__info_attribute_label">Port</td>
                <td>{detailData?.datasource?.datasource_port}</td>
              </tr>
            }
            {detailData.datasource?.datasource_type?.code_name?.includes("rdbms") &&
              <tr key="datasource_database">
                <td className="datasource__info_attribute_label">Database</td>
                <td>{detailData?.datasource?.datasource_database}</td>
              </tr>
            }
            {detailData?.datasource?.datasource_type?.code_name === "alert_telegram" &&
              <tr key="datasource_default_chat_id">
                <td className="datasource__info_attribute_label">Chat ID</td>
                <td>{detailData?.datasource?.datasource_host}</td>
              </tr>
            }
            {detailData?.datasource?.datasource_type?.code_name === "alert_email" &&
              <tr key="datasource_default_email">
                <td className="datasource__info_attribute_label">Eamil</td>
                <td>{detailData?.datasource?.datasource_username}</td>
              </tr>
            }
            <tr key="datasource_creation_time">
              <td className="datasource__info_attribute_label">Created at</td>
              <td>{detailData?.datasource?.created_on}</td>
            </tr>
            <tr key="datasource_last_modified">
              <td className="datasource__info_attribute_label">Last modified</td>
              <td>{detailData?.datasource?.changed_on}</td>
            </tr>
            {!detailData.datasource?.datasource_type?.code_name?.includes("alert_") &&
              <tr key="datasource_last_scan_metadata">
                <td className="datasource__info_attribute_label">Last scan metadata at</td>
                <td>{detailData?.datasource?.last_scan_metadata}</td>
              </tr>
            }
          </tbody>
        </Table>
      </Row>
      {detailData.datasource?.datasource_type?.code_name === "google_bigquery" &&
      <Container className='data-dictionary-listing'>
        <Row>
          <Col xs={12}>
            <FormGroup>
              <Input
                onKeyPress={(e: any) => {
                  if (e.code == "Enter") {
                    fetchData();
                  }
                }}
                placeholder="Search by name, code name, description... of dataset"
                onChange={(e) => {
                  setSearchKeyword(e.target.value);
                  setListingData({
                    ...listingData,
                    ...{ page: 0 },
                  });
                }}
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col xs={6}>
            <Button
              color="primary"
              onClick={() => {
                fetchData();
              }}
            >
              Search
            </Button>
          </Col>
        </Row>
        <Row>
          <Col xs={12}>
            <ReactPaginate
              previousLabel={"<"}
              nextLabel={">"}
              breakLabel={"..."}
              breakClassName={"break-me"}
              pageCount={Math.floor(listingData?.total / PAGE_LIMIT) + 1}
              marginPagesDisplayed={3}
              pageRangeDisplayed={2}
              initialPage={0}
              onPageChange={async (page) => {
                setListingData({
                  ...listingData,
                  ...{ page: page.selected },
                });
              }}
              containerClassName={"pagination"}
              activeClassName={"active"}
            />
          </Col>
        </Row>
        <Row>
          <Col xs={12}>
            <br />
            <Table responsive striped>
              <thead>
                <tr>
                  <th>Dataset Name</th>
                  <th>Code Name</th>
                  <th>Description</th>
                  <th>Last Edit By</th>
                  {canViewChangelog &&
                  <th></th>
                  }
                </tr>
              </thead>
              <tbody>
                {listingData.datasets.map((dataset) => (
                  <tr key={`${dataset.id}`}>
                    <td>
                      <a href={`/dataset/${dataset.id}`}>
                        <span>{dataset?.name}</span>
                      </a>
                      <br/>
                      {dataset.labels &&
                        Object.keys(dataset.labels).map(key => (
                          <span key={key} className="item__label">{key}:<b>{dataset.labels[key]}</b><br/></span>
                        ))
                      }
                    </td>
                    <td>{dataset.code_name}</td>
                    <td className='custom-textarea-multi-line'>
                      <EditbaleInput
                        index={"dataset"}
                        id={dataset.id}
                        updateKey={"description"}
                        updateValue={dataset?.description}
                      />
                    </td>
                    <td>{dataset.last_edit_by_email}</td>
                    {canViewChangelog &&
                      <td>
                        <a href={`/dictionary/changelog/dataset/${dataset.id}`}>
                          changelog
                        </a>
                      </td>
                    }
                  </tr>
                ))}
              </tbody>
            </Table>
          </Col>
        </Row>
      </Container>
      }
    </Container>
  );
}
